import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Skeleton,
  Avatar,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  PlayCircleFilled,
  CheckCircle,
  LockOutlined,
  DescriptionOutlined,
  OndemandVideo,
  AccessTime,
  MenuBook,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import color from "../../components/utils/Colors";
import { getOneDataFromTable, getDataFromTable} from "../../../src/services/services"

interface Content {
  id: string;
  lectureName: string;
  lectureDesc?: string;
  contentType?: string;
  lectureVideo?: string;
  duration?: string;
  isCompleted?: boolean;
  isLocked?: boolean;
}

const LecturePage: React.FC = () => {
  const navigate = useNavigate();
  const { courseId, lectureId } = useParams<{ courseId: string; lectureId: string }>();

  const [course, setCourse] = useState<any>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const [currentLecture, setCurrentLecture] = useState<Content | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    setLoading(true);
    Promise.allSettled([
      getOneDataFromTable("course", courseId),
      getDataFromTable("courseContent", { courseId }),
    ])
      .then(([courseRes, contentRes]) => {
        if (courseRes.status === "fulfilled") {
          setCourse(courseRes.value?.data?.data || {});
        }
        if (contentRes.status === "fulfilled") {
          const list: Content[] = contentRes.value?.data?.data || [];
          setContents(list);
          const found = list.find((c) => c.id === lectureId) || list[0] || null;
          setCurrentLecture(found);
          console.log(found)
        }
      })
      .finally(() => setLoading(false));
  }, [courseId, lectureId]);

  const currentIndex = contents.findIndex((c) => c.id === currentLecture?.id);
  const prevLecture = currentIndex > 0 ? contents[currentIndex - 1] : null;
  const nextLecture = currentIndex < contents.length - 1 ? contents[currentIndex + 1] : null;
  const completedCount = contents.filter((c) => c.isCompleted).length;
  const progress = contents.length > 0 ? (completedCount / contents.length) * 100 : 0;

  const navigateToLecture = (lecture: Content) => {
    navigate(`/course/${courseId}/lecture/${lecture.id}`);
    setCurrentLecture(lecture);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#F8F5FD",
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, md: 4 },
          py: 1.5,
          background: "rgba(241, 235, 249, 1)",
          borderBottom: "1.5px solid",
          borderColor: color.borderColor,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate(`/course/${courseId}`)}
            sx={{
              color: "rgba(46, 10, 87, 1)",
              border: "1.5px solid",
              borderColor: color.borderColor,
              borderRadius: "50%",
              p: 0.5,
            }}
          >
            <ChevronLeft />
          </IconButton>
          {loading ? (
            <Skeleton width={160} height={24} />
          ) : (
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: "rgba(46, 10, 87, 1)", fontSize: { xs: "13px", sm: "16px" } }}
            >
              {course?.title || course?.courseName || "Course"}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "gray", fontWeight: 600 }}>
              {completedCount}/{contents.length} completed
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: 100,
                borderRadius: 4,
                height: 6,
                backgroundColor: "#E0D4F5",
                "& .MuiLinearProgress-bar": { backgroundColor: "rgba(46, 10, 87, 1)" },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Main layout */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Left: video + details */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, md: 5 },
            py: 3,
          }}
        >
          {/* Video player */}
          <Box
            sx={{
              width: "100%",
              borderRadius: "16px",
              overflow: "hidden",
              background: "#1a0a2e",
              aspectRatio: "16/9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              boxShadow: "0 8px 32px rgba(46,10,87,0.18)",
            }}
          >
            {loading ? (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            ) : currentLecture?.lectureVideo ? (
              <video
                key={currentLecture.id}
                controls
                autoPlay
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                src={`${process.env.REACT_APP_PUBLIC_BASE_URL}${currentLecture.lectureVideo}`}
              />
            ) : (
              <Box sx={{ textAlign: "center", color: "rgba(255,255,255,0.4)" }}>
                <PlayCircleFilled sx={{ fontSize: 72, mb: 1, opacity: 0.5 }} />
                <Typography variant="body2">No video available for this lecture</Typography>
              </Box>
            )}
          </Box>

          {/* Prev / Next navigation under video */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2,
            }}
          >
            <Box
              onClick={() => prevLecture && navigateToLecture(prevLecture)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: prevLecture ? "pointer" : "default",
                opacity: prevLecture ? 1 : 0.35,
                px: 2,
                py: 1,
                borderRadius: "10px",
                border: "1.5px solid",
                borderColor: color.borderColor,
                background: "white",
                transition: "box-shadow 0.2s",
                "&:hover": prevLecture
                  ? { boxShadow: "0 2px 12px rgba(46,10,87,0.10)" }
                  : {},
                maxWidth: "45%",
              }}
            >
              <ChevronLeft sx={{ color: "rgba(46,10,87,1)", flexShrink: 0 }} />
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "gray", display: "block" }}>
                  Previous
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: "rgba(46,10,87,1)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {prevLecture?.lectureName || "—"}
                </Typography>
              </Box>
            </Box>

            <Box
              onClick={() => nextLecture && navigateToLecture(nextLecture)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: nextLecture ? "pointer" : "default",
                opacity: nextLecture ? 1 : 0.35,
                px: 2,
                py: 1,
                borderRadius: "10px",
                border: "1.5px solid",
                borderColor: color.borderColor,
                background: "white",
                transition: "box-shadow 0.2s",
                "&:hover": nextLecture
                  ? { boxShadow: "0 2px 12px rgba(46,10,87,0.10)" }
                  : {},
                maxWidth: "45%",
                textAlign: "right",
              }}
            >
              <Box sx={{ overflow: "hidden" }}>
                <Typography variant="caption" sx={{ color: "gray", display: "block" }}>
                  Next
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    color: "rgba(46,10,87,1)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {nextLecture?.lectureName || "—"}
                </Typography>
              </Box>
              <ChevronRight sx={{ color: "rgba(46,10,87,1)", flexShrink: 0 }} />
            </Box>
          </Box>

          {/* Lecture title + meta */}
          {loading ? (
            <>
              <Skeleton width="60%" height={36} sx={{ mb: 1 }} />
              <Skeleton width="30%" height={22} />
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: "rgba(46,10,87,1)", mb: 1, lineHeight: 1.3 }}
              >
                {currentLecture?.lectureName || "Lecture"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                {currentLecture?.contentType && (
                  <Chip
                    icon={<OndemandVideo sx={{ fontSize: 14 }} />}
                    label={currentLecture.contentType}
                    size="small"
                    sx={{
                      background: "rgba(46,10,87,0.08)",
                      color: "rgba(46,10,87,1)",
                      fontWeight: 600,
                      fontSize: "11px",
                    }}
                  />
                )}
                {currentLecture?.duration && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTime sx={{ fontSize: 14, color: "gray" }} />
                    <Typography variant="caption" sx={{ color: "gray" }}>
                      {currentLecture.duration}
                    </Typography>
                  </Box>
                )}
                {currentLecture?.isCompleted && (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 14, color: "#2e7d32 !important" }} />}
                    label="Completed"
                    size="small"
                    sx={{ background: "#e8f5e9", color: "#2e7d32", fontWeight: 600, fontSize: "11px" }}
                  />
                )}
              </Box>
            </>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <MenuBook sx={{ color: "rgba(46,10,87,0.7)", fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: "rgba(46,10,87,1)" }}>
                About this lecture
              </Typography>
            </Box>
            {loading ? (
              <>
                <Skeleton width="100%" />
                <Skeleton width="80%" />
                <Skeleton width="60%" />
              </>
            ) : (
              <Typography
                variant="body1"
                sx={{ color: "#4a4a6a", lineHeight: 1.8, fontSize: "15px" }}
              >
                {currentLecture?.lectureDesc || "No description available for this lecture."}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right sidebar: course playlist */}
        <Box
          sx={{
            width: { xs: "0px", md: "340px" },
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            borderLeft: "1.5px solid",
            borderColor: color.borderColor,
            background: "white",
            overflowY: "auto",
            flexShrink: 0,
          }}
        >
          {/* Sidebar header */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              background: "rgba(241, 235, 249, 1)",
              borderBottom: "1.5px solid",
              borderColor: color.borderColor,
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: "rgba(46,10,87,1)" }}>
              Course Content
            </Typography>
            <Typography variant="caption" sx={{ color: "gray" }}>
              {completedCount} of {contents.length} lectures completed
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                mt: 1,
                borderRadius: 4,
                height: 5,
                backgroundColor: "#E0D4F5",
                "& .MuiLinearProgress-bar": { backgroundColor: "rgba(46, 10, 87, 1)" },
              }}
            />
          </Box>

          {/* Lecture list */}
          <Box sx={{ flex: 1 }}>
            {loading
              ? [1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ px: 3, py: 2, borderBottom: "1px solid #f0ebf8" }}>
                    <Skeleton width="70%" height={18} />
                    <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                ))
              : contents.map((lecture, idx) => {
                  const isActive = lecture.id === currentLecture?.id;
                  return (
                    <Box
                      key={lecture.id}
                      onClick={() => !lecture.isLocked && navigateToLecture(lecture)}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #f0ebf8",
                        cursor: lecture.isLocked ? "not-allowed" : "pointer",
                        background: isActive ? "rgba(46,10,87,0.06)" : "transparent",
                        borderLeft: isActive ? "3px solid rgba(46,10,87,1)" : "3px solid transparent",
                        transition: "background 0.15s",
                        "&:hover": !lecture.isLocked && !isActive
                          ? { background: "rgba(46,10,87,0.03)" }
                          : {},
                      }}
                    >
                      {/* Index / status icon */}
                      <Box sx={{ mt: 0.5, flexShrink: 0 }}>
                        {lecture.isLocked ? (
                          <LockOutlined sx={{ fontSize: 18, color: "#ccc" }} />
                        ) : lecture.isCompleted ? (
                          <CheckCircle sx={{ fontSize: 18, color: "#4caf50" }} />
                        ) : isActive ? (
                          <PlayCircleFilled sx={{ fontSize: 18, color: "rgba(46,10,87,1)" }} />
                        ) : (
                          <Avatar
                            sx={{
                              width: 18,
                              height: 18,
                              fontSize: "10px",
                              background: "rgba(46,10,87,0.12)",
                              color: "rgba(46,10,87,1)",
                              fontWeight: 700,
                            }}
                          >
                            {idx + 1}
                          </Avatar>
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          fontWeight={isActive ? 700 : 500}
                          sx={{
                            color: lecture.isLocked
                              ? "#aaa"
                              : isActive
                              ? "rgba(46,10,87,1)"
                              : "#333",
                            lineHeight: 1.4,
                            mb: 0.5,
                          }}
                        >
                          {lecture.lectureName || `Lecture ${idx + 1}`}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {lecture.contentType && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              {lecture.contentType?.toLowerCase().includes("video") ? (
                                <OndemandVideo sx={{ fontSize: 12, color: "gray" }} />
                              ) : (
                                <DescriptionOutlined sx={{ fontSize: 12, color: "gray" }} />
                              )}
                              <Typography variant="caption" sx={{ color: "gray", fontSize: "11px" }}>
                                {lecture.contentType}
                              </Typography>
                            </Box>
                          )}
                          {lecture.duration && (
                            <Typography variant="caption" sx={{ color: "#bbb", fontSize: "11px" }}>
                              · {lecture.duration}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {isActive && (
                        <Tooltip title="Now playing">
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "rgba(46,10,87,1)",
                              mt: 1,
                              flexShrink: 0,
                              animation: "pulse 1.5s infinite",
                              "@keyframes pulse": {
                                "0%": { opacity: 1 },
                                "50%": { opacity: 0.3 },
                                "100%": { opacity: 1 },
                              },
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LecturePage;